import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./SubscriptionBookingModal.scss"; // Using the same SCSS file as before
import { monthNames, weekDays } from "../../SourceData";
import { LockClock } from "@mui/icons-material";
import moment from "moment";
import api from "../../utils/api";
import { toast } from "react-toastify";

const SubscriptionBookingModal = ({
  open,
  onClose,
  onBookingComplete,
  categoryId,
  bookingId,
}) => {
  // ============ STATE MANAGEMENT ============
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);
  const [bookingAvailability, setBookingAvailability] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workingHours, setWorkingHours] = useState({
    from: "09:00",
    to: "17:00",
  });
  // States for services
  const [currentStep, setCurrentStep] = useState("datetime"); // categories, services, datetime
  const [currentTime, setCurrentTime] = useState(new Date());
  // ============ HOOKS ============
  const navigate = useNavigate();
  // Helper function to check if current time is past working hours
  function isTimePastWorkingHours(currentTime, closingTimeStr) {
    const [closingHour, closingMinute] = closingTimeStr.split(":").map(Number);
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();

    return (
      currentHour > closingHour ||
      (currentHour === closingHour && currentMinute >= closingMinute)
    );
  }
  // Add helper function to check if a time slot is in the past
  function isTimeSlotPast(timeSlot) {
    // Only check for today
    if (
      !selectedDate ||
      selectedDate.toDateString() !== new Date().toDateString()
    ) {
      return false;
    }

    // Parse the end time of the slot
    const [hours, minutes] = timeSlot.endTime.split(":").map(Number);

    // Create a date object for the slot's end time
    const slotEndTime = new Date();
    slotEndTime.setHours(hours, minutes, 0, 0);

    // Compare with current time
    return currentTime >= slotEndTime;
  }
  // ============ INITIALIZE MODAL ============
  useEffect(() => {
    if (open) {
      setCurrentStep("datetime");
      // Reset error state
      setError(null);

      // Set today's date as default selected date when modal opens
      const today = new Date();
      setSelectedDate(today);
      setCurrentTime(today);
      // Make sure current month is set to display the current month
      setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    }
  }, [open]);

  // Add interval to update current time every minute
  useEffect(() => {
    if (!open) return;

    // Initial current time setting
    setCurrentTime(new Date());

    // Set up an interval to update current time every minute
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // 60000 ms = 1 minute

    // Clean up interval on unmount or when modal closes
    return () => clearInterval(intervalId);
  }, [open]);

  // Fetch available booking slots for selected date and category
  const fetchAvailableSlots = async (date) => {
    if (!date) return;
    setIsLoading(true);
    setError(null);
    try {
      const formattedDate = formatDateForAPI(date);
      console.log(`Fetching busy slots for date: ${formattedDate}`);
      const response = await api.get(`/bookings/busySlots`, {
        params: {
          date: formattedDate,
          categoryId: categoryId,
        },
      });
      const {
        bookingAvailability,
        workingHours: apiWorkingHours,
        visitPeriod,
        fullyBookedSlots,
      } = response.data.data;
      // Store working hours for further use
      setWorkingHours(apiWorkingHours);
      // Check if today is past working hours
      const today = new Date();
      if (
        date.toDateString() === today.toDateString() &&
        isTimePastWorkingHours(today, apiWorkingHours.to)
      ) {
        setBookingAvailability(false);
        setAvailableTimes([]);
        setBookedTimes([]);
        return;
      }
      setBookingAvailability(bookingAvailability);
      // Generate available and booked time slots
      const { availableSlots, bookedSlots } = generateTimeSlots(
        apiWorkingHours,
        visitPeriod,
        fullyBookedSlots
      );
      setAvailableTimes(availableSlots);
      setBookedTimes(bookedSlots);
    } catch (error) {
      console.error("Error fetching available slots:", error);
      setError("فشل في تحميل المواعيد المتاحة");
      setAvailableTimes([]);
      setBookedTimes([]);
      setBookingAvailability(false);
    } finally {
      setIsLoading(false);
    }
  };

  // ============ DATE & TIME FORMATTING ============
  function formatDateForAPI(date) {
    if (!date) return "";
    // Use these methods instead of toISOString() to preserve the local date
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD in local time
  }

  function formatTime(timeString) {
    if (!timeString || !timeString.includes(":")) {
      console.error("Invalid time format:", timeString);
      return timeString || "";
    }

    const [hours, minutes] = timeString.split(":").map(Number);

    // Handle invalid time values
    if (isNaN(hours) || isNaN(minutes)) {
      console.error("Invalid time components:", timeString);
      return timeString;
    }

    const period = hours >= 12 ? "مساءً" : "صباحًا";
    const displayHours = hours % 12 || 12;

    // Return formatted time in Arabic format
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  }

  function getFormattedDate(date) {
    if (!date) return "";
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("ar-SA", options);
  }

  // ============ TIME SLOT GENERATION ============
  function generateTimeSlots(workingHours, visitPeriod, fullyBookedSlots) {
    // Convert working hours to minutes
    const [startHour, startMinute] = workingHours.from.split(":").map(Number);
    const [endHour, endMinute] = workingHours.to.split(":").map(Number);

    // Convert visit period to minutes
    const visitPeriodMinutes = visitPeriod * 60;

    // Calculate total working minutes
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    const availableSlots = [];
    const bookedSlots = [];

    // Normalize booked slots for easier comparison
    const normalizedBookedSlots = Array.isArray(fullyBookedSlots)
      ? fullyBookedSlots.map((slot) => {
          // Handle different time formats (9:00 vs 09:00)
          const normalizedStartTime = slot.startTime.includes(":")
            ? slot.startTime
            : `${slot.startTime}:00`;

          return {
            ...slot,
            startTime: normalizedStartTime,
            // Convert times to lowercase and trim for consistent comparison
            startTimeNormalized: normalizedStartTime.toLowerCase().trim(),
          };
        })
      : [];

    // Generate time slots based on visit period
    for (
      let currentMinutes = startMinutes;
      currentMinutes + visitPeriodMinutes <= endMinutes;
      currentMinutes += visitPeriodMinutes
    ) {
      // Calculate start and end times
      const startHour = Math.floor(currentMinutes / 60);
      const startMinute = currentMinutes % 60;
      const endCalculatedMinutes = currentMinutes + visitPeriodMinutes;
      const endHour = Math.floor(endCalculatedMinutes / 60);
      const endMinute = endCalculatedMinutes % 60;

      // Format time strings
      const startTime = `${startHour.toString().padStart(2, "0")}:${startMinute
        .toString()
        .padStart(2, "0")}`;
      const endTime = `${endHour.toString().padStart(2, "0")}:${endMinute
        .toString()
        .padStart(2, "0")}`;

      // Check if this slot is fully booked - with improved comparison
      const isBooked = normalizedBookedSlots.some((slot) => {
        // Try different formats of time comparison to ensure matching
        return (
          // Direct comparison
          (slot.startTimeNormalized === startTime.toLowerCase() ||
            // Compare without leading zeros
            slot.startTimeNormalized ===
              `${parseInt(startTime.split(":")[0])}:${
                startTime.split(":")[1]
              }`.toLowerCase() ||
            // Compare with different separators
            slot.startTimeNormalized.replace(":", "") ===
              startTime.replace(":", "").toLowerCase()) &&
          slot.allTechniciansBusy
        );
      });

      const timeSlot = {
        formatted: formatTime(startTime),
        value: startTime,
        endTime: endTime,
        formattedEndTime: formatTime(endTime),
      };

      // Sort into appropriate array based on availability
      if (isBooked) {
        bookedSlots.push(timeSlot);
      } else {
        availableSlots.push(timeSlot);
      }
    }

    return { availableSlots, bookedSlots };
  }

  // ============ CALENDAR GENERATION ============
  useEffect(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // First and last day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Adjust for RTL calendar (week starts on Saturday)
    let firstDayOfWeek = (firstDay.getDay() + 1) % 7;

    const days = [];

    // Add empty slots for days before first day of month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ day: null, date: null });
    }

    // Add days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      const today = new Date();
      // Reset time to midnight for accurate date comparison
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);

      // Only disable past dates
      const isPastDate = date < today;

      days.push({
        day: i,
        date: date,
        isDisabled: isPastDate,
        isToday: date.toDateString() === today.toDateString(),
      });
    }

    setCalendarDays(days);
  }, [currentMonth]);

  // ============ EFFECTS ============
  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate && currentStep === "datetime") {
      fetchAvailableSlots(selectedDate);
    } else {
      setAvailableTimes([]);
      setBookedTimes([]);
    }
  }, [selectedDate, currentStep]);

  // ============ EVENT HANDLERS ============
  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  // Handle date selection
  const handleDateClick = (day) => {
    if (day && !day.isDisabled) {
      setSelectedDate(day.date);
      setSelectedTime(null); // Reset time when date changes
    }
  };

  // Handle time selection
  const handleTimeClick = (time) => {
    // Don't allow selecting past time slots
    if (isTimeSlotPast(time)) {
      return;
    }

    setSelectedTime(time);
  };

  // Confirm booking using subscription method
  const handleConfirmBooking = async () => {
    if (!selectedDate || !selectedTime) return;
    setIsLoading(true);
    setError(null);

    try {
      // Prepare data for API call
      const bookingData = {
        date: formatDateForAPI(selectedDate),
        startTime: selectedTime.value,
      };
      // Make API call to create booking using subscription
      await api.patch(`/admin/bookings/${bookingId}/update`, bookingData);
      // Close modal
      onClose();
      onBookingComplete();
      toast.success("تم اعادة جدولة الطلب بنجاح");
    } catch (error) {
      console.error("Error creating booking:", error);
      setError("فشل في اعادة الجدولة");
    } finally {
      setIsLoading(false);
    }
  };

  // Render time selection
  const renderTimeSelection = () => {
    const formattedFromTime = moment(workingHours.from, "HH:mm").format(
      "h:mm A"
    );
    const formattedToTime = moment(workingHours.to, "HH:mm").format("h:mm A");

    if (isLoading) {
      return <div className="loading-state">جاري التحميل...</div>;
    }

    if (error) {
      return <div className="error-state">هناك خطأ ما</div>;
    }

    // Special handling for today being past working hours
    const today = new Date();
    if (
      selectedDate &&
      selectedDate.toDateString() === today.toDateString() &&
      !bookingAvailability
    ) {
      return (
        <div className="working-hours-container">
          <div className="working-hours-header">
            <LockClock className="working-hours-icon" />
            <h3 className="working-hours-title">ساعات العمل</h3>
          </div>
          <div className="working-hours-display">
            <div className="working-hours-time">
              <span className="working-hours-label">من:</span>
              <span className="working-hours-value">{formattedFromTime}</span>
            </div>
            <div className="working-hours-time">
              <span className="working-hours-label">إلى:</span>
              <span className="working-hours-value">{formattedToTime}</span>
            </div>
          </div>
          <div className="no-availability">
            لا توجد مواعيد متاحة لليوم الحالي
          </div>
        </div>
      );
    }

    if (!bookingAvailability) {
      return <div className="no-availability">لا توجد مواعيد متاحة</div>;
    }

    return (
      <div className="time-selection">
        {/* Section Title */}
        <h4 className="field-label">
          {selectedDate ? "الأوقات المتاحة" : "الرجاء اختيار تاريخ"}
        </h4>

        {/* Available time slots */}
        {availableTimes.length > 0 && (
          <div className="available-slots">
            <h5 className="slots-heading">المواعيد المتاحة</h5>
            <div className="time-grid">
              {availableTimes.map((time) => {
                const isPastTimeSlot = isTimeSlotPast(time);
                return (
                  <button
                    key={time.value}
                    className={`time-option ${
                      selectedTime && selectedTime.value === time.value
                        ? "selected"
                        : ""
                    } ${isPastTimeSlot ? "past-time" : ""}`}
                    onClick={() => handleTimeClick(time)}
                    aria-selected={
                      selectedTime && selectedTime.value === time.value
                    }
                    disabled={isPastTimeSlot}
                    aria-disabled={isPastTimeSlot}
                  >
                    <>
                      <span className="time-range">
                        من {time.formatted} إلى {time.formattedEndTime}
                      </span>
                      {isPastTimeSlot && (
                        <span className="time-status no-wrap">وقت منتهي</span>
                      )}
                    </>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Fully booked time slots */}
        {bookedTimes.length > 0 && (
          <div className="booked-slots">
            <h5 className="slots-heading">المواعيد المحجوزة</h5>
            <div className="time-grid booked-grid">
              {bookedTimes.map((time) => {
                const isPastTimeSlot = isTimeSlotPast(time);
                return (
                  <button
                    key={time.value}
                    className={`time-option booked ${
                      isPastTimeSlot ? "past-time" : ""
                    }`}
                    disabled
                    aria-disabled="true"
                  >
                    <>
                      <span className="time-range">
                        من {time.formatted} إلى {time.formattedEndTime}
                      </span>
                      {isPastTimeSlot ? (
                        <span className="time-status no-wrap">وقت منتهي</span>
                      ) : (
                        <span className="time-status no-wrap">محجوز</span>
                      )}
                    </>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {availableTimes.length === 0 &&
          bookedTimes.length === 0 &&
          !isLoading &&
          !error &&
          selectedDate && (
            <div className="no-times">لا توجد مواعيد متاحة لهذا اليوم</div>
          )}
      </div>
    );
  };

  // Don't render if modal is closed
  if (!open) return null;

  // ============ RENDER ============
  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="booking-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="booking-modal-title"
        >
          <div className="modal-header">
            <h2 className="modal-title" id="booking-modal-title">
              اعادة جدولة طلب
            </h2>
            <button
              className="close-button"
              onClick={onClose}
              aria-label="إلغاء"
            >
              <span>×</span>
            </button>
          </div>

          <div className="booking-content">
            {/* Step: Date and Time Selection */}
            <div className="date-time-selection">
              <h3 className="section-title">اختر التاريخ والوقت</h3>

              {/* Calendar */}
              <div
                className="custom-calendar"
                role="application"
                aria-label="التقويم"
              >
                <div className="calendar-header">
                  <button
                    className="month-nav"
                    onClick={prevMonth}
                    aria-label="الشهر السابق"
                  >
                    &#9656;
                  </button>
                  <h4 className="current-month">
                    {monthNames[currentMonth.getMonth()]}{" "}
                    {currentMonth.getFullYear()}
                  </h4>
                  <button
                    className="month-nav"
                    onClick={nextMonth}
                    aria-label="الشهر التالي"
                  >
                    &#9666;
                  </button>
                </div>

                <div className="calendar-weekdays" role="row">
                  {weekDays.map((day) => (
                    <div key={day} className="weekday" role="columnheader">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="calendar-days" role="grid">
                  {calendarDays.map((day, index) => (
                    <div
                      key={index}
                      className={`calendar-day ${!day.day ? "empty" : ""} ${
                        day.isDisabled ? "disabled" : ""
                      } ${day.isToday ? "today" : ""} ${
                        selectedDate &&
                        day.date &&
                        day.date.toDateString() === selectedDate.toDateString()
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => handleDateClick(day)}
                      tabIndex={day.day && !day.isDisabled ? 0 : -1}
                      role={day.day ? "gridcell" : "presentation"}
                      aria-selected={
                        selectedDate &&
                        day.date &&
                        day.date.toDateString() === selectedDate.toDateString()
                      }
                      aria-disabled={day.isDisabled}
                    >
                      {day.day}
                    </div>
                  ))}
                </div>
              </div>

              {/* Time Selection */}
              {renderTimeSelection()}

              {/* Booking Confirmation */}
              {selectedDate && selectedTime && (
                <div className="booking-confirmation" aria-live="polite">
                  <p className="confirmation-text">
                    سيتم تأكيد حجزك ليوم {getFormattedDate(selectedDate)} من
                    الساعة {selectedTime.formatted} إلى{" "}
                    {selectedTime.formattedEndTime}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button className="cancel-btn" onClick={onClose}>
              إلغاء
            </button>

            {currentStep === "datetime" && (
              <button
                className={`confirm-btn ${
                  !selectedDate || !selectedTime || isLoading ? "disabled" : ""
                }`}
                disabled={!selectedDate || !selectedTime || isLoading}
                onClick={handleConfirmBooking}
              >
                {isLoading ? "جاري التحميل..." : "تأكيد الحجز"}
              </button>
            )}
          </div>

          {/* Show global error message */}
          {error && (
            <div className="modal-error" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SubscriptionBookingModal;
